import { FC, PropsWithChildren, createContext, useContext } from "react"
import { useSocketIO } from "../../providers/sockets/SocketIOProvider"
import lightswarmMapper from "./data/lightswarmMapper"

type Unit = {
  Name: string
  Beds?: number
  Level?: number
}

interface TurnLightsOptions {
  hasAmenities?: string[]
  hasFavourites?: boolean
  favouriteUnits?: any
}

type LightswarmContextType = {
  /**
   * Turn off specific lights or group of lights.
   * @param units List of units to turn off.
   * @returns void
   */
  turnLightsOff: (units: Unit[]) => void
  /**
   * Turn on specific lights or group of lights.
   * @param {Array} units List of units to turn on.
   * @param {{ isFav: boolean; dimmedUnits: any }} favourites If it's for favorites, provide dimmedUnits.
   * @param {boolean} isAmenity If true, it's for amenities.
   * @param {string} room Room to turn on lights in.
   */
  turnLightsOn: (
    units: Unit[] | "allOn",
    room?: string,
    options?: TurnLightsOptions,
  ) => void
  turnAllLightsOff: () => void
  turnAllLightsOn: () => void
}

const LightswarmContext = createContext<LightswarmContextType>({
  turnLightsOff: () => {},
  turnLightsOn: () => {},
  turnAllLightsOff: () => {},
  turnAllLightsOn: () => {},
})

export const LightswarmProvider: FC<PropsWithChildren> = ({ children }) => {
  const { emitSocketEvent } = useSocketIO()

  const turnLightsOff = (units: Unit[]) => {
    emitSocketEvent("lights", units)
  }
  const turnLightsOn = (
    units: Unit[] | "allOn",
    room?: string,
    options?: TurnLightsOptions,
  ) => {
    let lights: {
      lightsOn: any
      lightsOff: any
      lightsDimmed?: any
    }

    const {
      hasAmenities = [],
      hasFavourites = false,
      favouriteUnits = [],
    } = options || {}

    if (hasAmenities?.length > 0) {
      lights = lightswarmMapper.lightUpSpecificAmenities(hasAmenities)
    } else if (hasFavourites) {
      lights = lightswarmMapper.lightUpFavourites(
        units as any | "allOn",
        favouriteUnits,
      )
    } else {
      lights = lightswarmMapper.resolveLightswarmAddr(units as any | "allOn")
    }

    emitSocketEvent?.("lights", lights)
  }
  const turnAllLightsOff = () => {
    emitSocketEvent?.("lights", [])
  }
  const turnAllLightsOn = () => {
    turnLightsOn("allOn")
  }

  return (
    <LightswarmContext.Provider
      value={{
        turnLightsOff,
        turnLightsOn,
        turnAllLightsOff,
        turnAllLightsOn,
      }}
    >
      {children}
    </LightswarmContext.Provider>
  )
}

export const useLightswarm = (): LightswarmContextType =>
  useContext(LightswarmContext)
